import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { ImagePlus, Trash2 } from "lucide-react";

export const MAX_IMAGE_COUNT = 10;

interface ImageUploaderProps {
  value: string[];
  onChange: (urls: string[]) => void;
  maxCount?: number;
}

/**
 * Cover-image picker shared by the event and activity wizards: a grid of
 * previews (the first marked as the cover) plus a dashed "add" tile that opens
 * a hidden multi-select file input. Files are read as `data:` URLs and uploaded
 * to Cloudinary by the calling service.
 */
const ImageUploader: React.FC<ImageUploaderProps> = ({
  value,
  onChange,
  maxCount = MAX_IMAGE_COUNT,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const room = maxCount - value.length;
    const picked = Array.from(files)
      .filter((file) => file.type.startsWith("image/"))
      .slice(0, Math.max(0, room));
    // FileReader is async, so read each picked file before appending it.
    picked.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        onChange([...value, String(reader.result ?? "")]);
      };
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <>
      <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-4">
        {value.map((image, index) => (
          <div
            key={`${image.slice(0, 32)}-${index}`}
            className="relative aspect-square overflow-hidden rounded-xl border border-gray-100"
          >
            <img src={image} alt="" className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => removeImage(index)}
              aria-label={t("Delete")}
              className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white transition hover:bg-black/75"
            >
              <Trash2 size={12} />
            </button>
            {index === 0 ? (
              <span className="absolute bottom-0 left-0 right-0 bg-[#ffa217] py-0.5 text-center text-[10px] font-bold text-white">
                {t("Cover")}
              </span>
            ) : null}
          </div>
        ))}

        {value.length < maxCount ? (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-gray-200 text-gray-400 transition hover:border-[#ffa217] hover:text-[#ffa217]"
          >
            <ImagePlus size={20} />
            <span className="text-[11px] font-semibold">{t("Add Image")}</span>
          </button>
        ) : null}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </>
  );
};

export default ImageUploader;
